import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Wine, MapPin, Calendar, CheckCircle, Clock, Gift } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DrinkHistoryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sent'); // 'sent' or 'received'
  const [sentDrinks, setSentDrinks] = useState([]);
  const [receivedDrinks, setReceivedDrinks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDrinkHistory();
  }, []);

  const fetchDrinkHistory = async () => {
    setLoading(true);
    try {
      const [sentRes, receivedRes] = await Promise.all([
        axios.get(`${API}/drinks/sent`),
        axios.get(`${API}/drinks/received`)
      ]);
      setSentDrinks(sentRes.data);
      setReceivedDrinks(receivedRes.data);
    } catch (error) {
      console.error('Error fetching drink history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'paid':
        return { label: 'Ready to Redeem', color: '#D7B56D', icon: Clock };
      case 'redeemed':
        return { label: 'Redeemed', color: '#4CAF50', icon: CheckCircle };
      case 'pending':
        return { label: 'Payment Pending', color: '#8A7355', icon: Clock };
      default:
        return { label: status, color: '#8A7355', icon: Clock };
    }
  };

  const renderDrinkCard = (drink, type) => {
    const statusInfo = getStatusInfo(drink.status);
    const StatusIcon = statusInfo.icon;
    const displayDate = type === 'sent' ? drink.paid_at || drink.created_at : drink.redeemed_at;

    return (
      <div
        key={drink.id}
        style={{
          background: 'rgba(230, 215, 195, 0.05)',
          border: '1px solid #A57C33',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '18px', color: '#E6D7C3', fontWeight: 'bold', marginBottom: '4px' }}>
              {drink.drink_name}
            </h3>
            <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '8px' }}>
              <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
              {drink.venue_name}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '18px', color: '#D7B56D', fontWeight: 'bold' }}>
              ${drink.drink_price.toFixed(2)}
            </p>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '12px',
          borderTop: '1px solid rgba(165, 124, 51, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <StatusIcon size={16} color={statusInfo.color} />
            <span style={{ color: statusInfo.color, fontSize: '14px', fontWeight: '500' }}>
              {statusInfo.label}
            </span>
          </div>
          {displayDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#8A7355', fontSize: '13px' }}>
              <Calendar size={14} />
              {formatDate(displayDate)} • {formatTime(displayDate)}
            </div>
          )}
        </div>

        {type === 'sent' && drink.recipient_info && (
          <div style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(215, 181, 109, 0.1)',
            borderRadius: '6px'
          }}>
            <p style={{ fontSize: '13px', color: '#A57C33', marginBottom: '2px' }}>Recipient</p>
            <p style={{ fontSize: '14px', color: '#E6D7C3' }}>{drink.recipient_info}</p>
          </div>
        )}

        {type === 'sent' && drink.personal_message && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            background: 'rgba(215, 181, 109, 0.1)',
            borderRadius: '6px'
          }}>
            <p style={{ fontSize: '13px', color: '#A57C33', marginBottom: '2px' }}>Message</p>
            <p style={{ fontSize: '14px', color: '#E6D7C3', fontStyle: 'italic' }}>"{drink.personal_message}"</p>
          </div>
        )}

        {type === 'sent' && drink.status === 'paid' && (
          <div style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(215, 181, 109, 0.15)',
            borderRadius: '6px',
            border: '1px solid #A57C33'
          }}>
            <p style={{ fontSize: '13px', color: '#D7B56D', fontWeight: '500' }}>
              QR Code: {drink.unique_code}
            </p>
          </div>
        )}

        {type === 'received' && drink.sender_name && (
          <div style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(215, 181, 109, 0.1)',
            borderRadius: '6px'
          }}>
            <p style={{ fontSize: '13px', color: '#A57C33', marginBottom: '2px' }}>From</p>
            <p style={{ fontSize: '14px', color: '#E6D7C3' }}>{drink.sender_name}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}
        >
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          DRINK HISTORY
        </h1>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '0 20px',
        marginBottom: '24px',
        borderBottom: '1px solid #A57C33'
      }}>
        <button
          onClick={() => setActiveTab('sent')}
          style={{
            flex: 1,
            padding: '12px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'sent' ? '2px solid #D7B56D' : '2px solid transparent',
            color: activeTab === 'sent' ? '#D7B56D' : '#8A7355',
            fontSize: '16px',
            fontWeight: activeTab === 'sent' ? 'bold' : 'normal',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <Gift size={18} />
          Sent ({sentDrinks.length})
        </button>
        <button
          onClick={() => setActiveTab('received')}
          style={{
            flex: 1,
            padding: '12px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'received' ? '2px solid #D7B56D' : '2px solid transparent',
            color: activeTab === 'received' ? '#D7B56D' : '#8A7355',
            fontSize: '16px',
            fontWeight: activeTab === 'received' ? 'bold' : 'normal',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <Wine size={18} />
          Received ({receivedDrinks.length})
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {loading ? (
          <p style={{ color: '#A57C33', textAlign: 'center', marginTop: '40px' }}>Loading...</p>
        ) : (
          <>
            {activeTab === 'sent' && (
              <>
                {sentDrinks.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#8A7355'
                  }}>
                    <Gift size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                    <p style={{ fontSize: '16px' }}>You haven't sent any drinks yet</p>
                    <button
                      onClick={() => navigate('/send-drink')}
                      style={{
                        marginTop: '20px',
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                        color: '#2C1B13',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Send Your First Drink
                    </button>
                  </div>
                ) : (
                  sentDrinks.map(drink => renderDrinkCard(drink, 'sent'))
                )}
              </>
            )}

            {activeTab === 'received' && (
              <>
                {receivedDrinks.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#8A7355'
                  }}>
                    <Wine size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                    <p style={{ fontSize: '16px' }}>You haven't received any drinks yet</p>
                  </div>
                ) : (
                  receivedDrinks.map(drink => renderDrinkCard(drink, 'received'))
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DrinkHistoryPage;
